import { cx } from "./utils";
import { Icon } from "./Icon";

/** One step. `locked` steps cannot be selected (T6 before T5 is submitted). */
export interface Step {
  id: string;
  label: string;
  /** Secondary line under the label. */
  hint?: string;
  done?: boolean;
  locked?: boolean;
}

export interface StepperProps {
  steps: readonly Step[];
  /** Index of the current step. */
  current: number;
  /** Called when a selectable (not locked) step is clicked. */
  onStepClick?: (index: number, step: Step) => void;
  /** Accessible name for the step list. */
  label?: string;
  className?: string;
}

function stateOf(s: Step, i: number, current: number): "locked" | "done" | "current" | "todo" {
  return s.locked ? "locked" : s.done ? "done" : i === current ? "current" : "todo";
}

/** Labels often start with their own number ("1 注册"); the compact line adds it. */
function bareLabel(label: string): string {
  return label.replace(/^\s*\d+[.、\s]*/, "");
}

/**
 * Step indicator. Two layouts are rendered and switched in CSS:
 * boxed steps on ≥768px, and — because four Chinese labels in narrow boxes
 * wrap to one character per line on a 375px phone — a single row of numbered
 * dots with only the current step named underneath on mobile.
 */
export function Stepper({ steps, current, onStepClick, label = "步骤", className }: StepperProps) {
  const now = steps[current];
  return (
    <div className={cx("xz-stepper-wrap", className)}>
      <ol className="xz-stepper" aria-label={label}>
        {steps.map((s, i) => {
          const state = stateOf(s, i, current);
          const clickable = !!onStepClick && !s.locked;
          return (
            <li
              key={s.id}
              className={cx("xz-stepper__step", `is-${state}`)}
              aria-current={i === current ? "step" : undefined}
            >
              <button
                type="button"
                className="xz-stepper__btn"
                disabled={!clickable}
                aria-disabled={s.locked || undefined}
                onClick={clickable ? () => onStepClick?.(i, s) : undefined}
              >
                <span className="xz-stepper__marker" aria-hidden="true">
                  {s.done ? <Icon id="ui-check" size={16} /> : i + 1}
                </span>
                <span className="xz-stepper__text">
                  <span className="xz-stepper__label">{s.label}</span>
                  {s.hint && <span className="xz-stepper__hint">{s.hint}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="xz-stepper--compact" role="group" aria-label={label}>
        <span className="xz-stepper__dots" aria-hidden="true">
          {steps.map((s, i) => (
            <span key={s.id} className={cx("xz-stepper__dot", `is-${stateOf(s, i, current)}`)}>
              {s.done ? <Icon id="ui-check" size={16} /> : i + 1}
            </span>
          ))}
        </span>
        <span className="xz-stepper__now">
          第 {current + 1} 步 · {bareLabel(now?.label ?? "")}
          <span className="xz-stepper__of">（共 {steps.length} 步）</span>
        </span>
      </div>
    </div>
  );
}
